import React from 'react';

const HowToUsePage = () => {
  const steps = [
    {
      number: '01',
      title: 'Paste Your Video Link',
      description: 'Copy and paste your YouTube video URL into the input field. Gradi.AI supports all types of YouTube videos including regular videos, shorts, and live streams.',
      icon: '🔗',
      color: '#8b5cf6'
    },
    {
      number: '02',
      title: 'Click Analyze',
      description: 'Hit the Analyze button and let our AI work its magic. Our advanced algorithms will process your video content and extract meaningful insights.',
      icon: '🚀',
      color: '#ec4899'
    },
    {
      number: '03',
      title: 'Get Detailed Insights',
      description: 'Receive comprehensive analysis including content quality scores, educational value assessment, and detailed feedback on various aspects of your video.',
      icon: '📊',
      color: '#06b6d4'
    },
    {
      number: '04',
      title: 'Apply Improvements',
      description: 'Use our actionable suggestions and recommendations to enhance your content, improve engagement, and create more effective educational materials.',
      icon: '✨',
      color: '#10b981'
    }
  ];

  const tips = [
    {
      icon: '🎯',
      title: 'Quality Matters',
      tip: 'Higher quality videos with clear audio get more accurate analysis results.'
    },
    {
      icon: '⏱️',
      title: 'Optimal Length',
      tip: 'Videos between 2-30 minutes provide the best balance for comprehensive analysis.'
    },
    {
      icon: '🔊',
      title: 'Clear Audio',
      tip: 'Ensure your video has clear, audible speech for better transcript analysis.'
    },
    {
      icon: '📝',
      title: 'Educational Content',
      tip: 'Videos with structured, educational content receive more detailed insights.'
    }
  ];

  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 40; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container how-to-use-page">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="how-to-use-content">
        {/* Header */}
        <div className="how-to-use-header">
          <h1 className="how-to-use-title">
            How to Use Gradi.AI
          </h1>
          <p style={{
            fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
            color: '#9ca3af',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Get started with AI-powered video analysis in just a few simple steps
          </p>
        </div>

        {/* Steps */}
        <div className="steps-grid">
          {steps.map((step, index) => (
            <div
              key={index}
              className="step-card"
              style={{
                border: `1px solid ${step.color}30`,
                boxShadow: `0 25px 50px rgba(0, 0, 0, 0.25), 0 0 30px ${step.color}20`,
                animation: `fadeInUp ${0.2 * (index + 1)}s ease-out`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px) scale(1.02)';
                e.currentTarget.style.boxShadow = `0 35px 70px rgba(0, 0, 0, 0.3), 0 0 50px ${step.color}40`;
                e.currentTarget.style.borderColor = `${step.color}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = `0 25px 50px rgba(0, 0, 0, 0.25), 0 0 30px ${step.color}20`;
                e.currentTarget.style.borderColor = `${step.color}30`;
              }}
            >
              {/* Glowing border effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${step.color}10, transparent, ${step.color}05)`,
                borderRadius: '24px',
                pointerEvents: 'none'
              }} />

              {/* Step Number */}
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                fontWeight: '900',
                color: 'white',
                boxShadow: `0 10px 20px ${step.color}40`,
                border: '3px solid rgba(255, 255, 255, 0.1)'
              }}>
                {step.number}
              </div>

              {/* Icon */}
              <div style={{
                fontSize: '3rem',
                marginBottom: '1.5rem',
                textAlign: 'center',
                filter: `drop-shadow(0 0 20px ${step.color}60)`
              }}>
                {step.icon}
              </div>

              {/* Title */}
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '1rem',
                color: step.color,
                textAlign: 'center',
                textShadow: `0 0 20px ${step.color}60`
              }}>
                {step.title}
              </h3>

              {/* Description */}
              <p style={{
                color: '#d1d5db',
                lineHeight: '1.7',
                fontSize: '1rem',
                margin: 0,
                textAlign: 'center'
              }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Additional Tips */}
        <div style={{
          background: 'rgba(15, 15, 35, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '3rem',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 50px rgba(139, 92, 246, 0.1)',
          marginBottom: '3rem',
          animation: 'fadeInUp 1s ease-out'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '2rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            💡 Pro Tips
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            {tips.map((tip, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                }}
              >
                <div style={{
                  fontSize: '2rem',
                  marginBottom: '1rem'
                }}>
                  {tip.icon}
                </div>
                <h4 style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  color: '#8b5cf6'
                }}>
                  {tip.title}
                </h4>
                <p style={{
                  color: '#d1d5db',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  {tip.tip}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div style={{
          textAlign: 'center',
          marginTop: '3rem'
        }}>
          <p style={{
            fontSize: '1.25rem',
            color: '#9ca3af',
            marginBottom: '2rem'
          }}>
            Ready to analyze your first video?
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
              fontSize: '1.2rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 15px 35px rgba(139, 92, 246, 0.3)',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
              e.target.style.boxShadow = '0 20px 45px rgba(139, 92, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 15px 35px rgba(139, 92, 246, 0.3)';
            }}
          >
            🚀 Start Analyzing Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowToUsePage;